import { Subject, filter, map, Observable } from 'rxjs'
import { shareReplay } from 'rxjs/operators'
import CableApp from 'actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'

/** Shape of an ActionCable subscription returned by `subscriptions.create`. */
interface ActionCableSubscription {
    perform(action: string, data?: Record<string, unknown>): boolean
    send(data: Record<string, unknown>): boolean
    unsubscribe(): void
    consumer?: {
        connection?: {
            isOpen(): boolean
        }
        subscriptions?: {
            subscriptions?: ActionCableSubscription[]
        }
    }
}

/** Payload carried by every EventBus message. */
export type EventBusPayload = Record<string, unknown>

export interface EventBusMessage {
    topic: string
    payload: EventBusPayload
    timestamp: number
}

interface FollowRequest {
    resource_type: 'answer' | 'quest' | 'user'
    resource_id: string
}

/** Params sent to ActionCable when subscribing to EventBusChannel. */
interface EventBusSubscriptionParams {
    channel: 'EventBusChannel'
    user_id: string
    answer_ids?: string[]
    quest_ids?: string[]
}

export class EventBusService {
    private eventBus$ = new Subject<EventBusMessage>()
    private channel: ActionCableSubscription | null = null
    private subscribedResources = new Set<string>()
    private connectionCallbacks = new Set<() => void>()

    constructor() {
        // Set up global logging for ALL EventBus messages
        this.eventBus$.subscribe({
            next: message => {
                // Only log workflow status messages, not all EventBus traffic
                const status = message.payload?.status as { message?: string } | undefined
                if (status?.message) {
                    if (message.topic === 'mint_ordinal_status') {
                        console.log(`Minting:`, status.message)
                    } else if (message.topic === 'send_ordinal_status') {
                        console.log(`Sending:`, status.message)
                    }
                }
            },
            error: err => {
                console.error(`🚌 [EventBus] Error in message stream:`, err)
            },
        })
    }

    connect(initialResources?: { answer_ids?: string[]; quest_ids?: string[] }) {
        if (this.channel) {
            return
        }

        const userId = window.gon?.currentUser?.id
        if (!userId) {
            // A guest page has nothing to subscribe as. Every consumer gates on
            // isConnected(), so staying disconnected is the whole answer — not
            // an error worth a console line on every mount.
            console.debug('🚌 [EventBus] Not connecting: no signed-in user')
            return
        }

        const params: EventBusSubscriptionParams = {
            channel: 'EventBusChannel',
            user_id: userId,
        }
        if (initialResources?.answer_ids) {
            params.answer_ids = initialResources.answer_ids
        }
        if (initialResources?.quest_ids) {
            params.quest_ids = initialResources.quest_ids
        }

        this.channel = CableApp.cable.subscriptions.create(withChannelAuth(params), {
            received: (data: EventBusMessage) => {
                // All messages should have a topic - inject into RxJS Subject
                if (data.topic) {
                    this.eventBus$.next(data)
                } else {
                    console.warn('🚌 [EventBus] Received data without topic:', data)
                }
            },

            connected: () => {
                console.log('✅ [EventBus] Connected')
                this.connectionCallbacks.forEach(cb => cb())
            },

            disconnected: () => {
                console.log('❌ [EventBus] Disconnected')
                this.subscribedResources.clear()
            },

            rejected: () => {
                console.error('🚫 [EventBus] Subscription rejected by server!')
            },
        })
    }

    disconnect() {
        if (this.channel) {
            this.channel.unsubscribe()
            this.channel = null
            this.subscribedResources.clear()
            console.log('❌ [EventBus] Disconnected')
        }
    }

    onConnected(callback: () => void): () => void {
        this.connectionCallbacks.add(callback)
        // If already connected, call immediately
        if (this.channel?.consumer?.connection?.isOpen()) {
            callback()
        }

        return () => {
            this.connectionCallbacks.delete(callback)
        }
    }

    // Get observable for specific topic
    getTopic$(topic: string): Observable<EventBusPayload> {
        return this.eventBus$.pipe(
            filter(msg => msg.topic === topic),
            map(msg => msg.payload),
            shareReplay({ bufferSize: 1, refCount: true }),
        )
    }

    // Get observable for specific topic and resource
    getResourceTopic$(
        topic: string,
        resourceType: string,
        resourceId: string,
    ): Observable<EventBusPayload> {
        return this.eventBus$.pipe(
            filter(msg => msg.topic === topic),
            map(msg => msg.payload),
            filter(payload => {
                // Check if payload is for this specific resource
                const resourceKey = `${resourceType}_id`
                return (
                    payload[resourceKey] === resourceId ||
                    payload[resourceKey] === parseInt(resourceId)
                )
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        )
    }

    // Get raw event bus for custom filtering
    getEventBus$(): Observable<EventBusMessage> {
        return this.eventBus$.asObservable()
    }

    follow(resourceType: FollowRequest['resource_type'], resourceId: string) {
        const key = `${resourceType}_${resourceId}`
        if (this.subscribedResources.has(key)) return
        this.subscribedResources.add(key)
        this.channel?.perform('follow', { resource_type: resourceType, resource_id: resourceId })
    }

    unfollow(resourceType: FollowRequest['resource_type'], resourceId: string) {
        const key = `${resourceType}_${resourceId}`
        if (!this.subscribedResources.has(key)) return
        this.subscribedResources.delete(key)
        this.channel?.perform('unfollow', { resource_type: resourceType, resource_id: resourceId })
    }

    isConnected(): boolean {
        return !!this.channel?.consumer?.connection?.isOpen()
    }

    isSubscribedTo(resourceType: string, resourceId: string): boolean {
        const key = `${resourceType}_${resourceId}`
        return this.subscribedResources.has(key)
    }
}

// Singleton instance
export const eventBusService = new EventBusService()

// Expose to window for debugging
if (typeof window !== 'undefined') {
    window.eventBusService = eventBusService
}
